import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";
import { ApiError } from "../../shared/api/client";
import { productionGateway } from "./production-gateway";
import { canRetryQuantityWrite, quantityAttemptFailed, quantityDraftSchema,
  type PendingQuantityWrite, type QuantityWriteDraft, type QuantityWriteIdentity } from "./quantity-write-queue";
import { claimQuantityOutbox, dismissRejectedQuantity, enqueueQuantityOutbox, finishQuantityAttempt,
  importLegacyQuantityOutbox, listQuantityOutbox, type LocalDraftRef } from "./quantity-outbox-store";
import { localDatabaseChangedEvent } from "./operator-local-database";

const rejectedCodes = new Set(["PRODUCTION_IDEMPOTENCY_MISMATCH", "PRODUCTION_UNIT_CONTRACT_CHANGED", "PRODUCTION_UNIT_INVALID",
  "PRODUCTION_UNIT_FACTOR_REQUIRED", "PRODUCTION_UNIT_UNAVAILABLE", "PRODUCTION_CONFLICT", "PRODUCTION_VALIDATION_ERROR",
  "PRODUCTION_SESSION_CHANGED", "VALIDATION_ERROR", "UNAUTHORIZED", "FORBIDDEN"]);
const receiptEntrySchema = z.object({ id: z.string().min(1), enteredBy: z.string(), operatorSessionId: z.string(), terminalId: z.string(),
  type: z.string(), quantity: z.number(), unit: z.string(), occurredAt: z.string() });
const receiptSchema = z.object({ status: z.enum(["CONFIRMED", "NOT_FOUND"]), entry: receiptEntrySchema.nullable() });

export function useQuantityWritesWorkspace({ identity, preview, connected, onConfirmed }: {
  identity: QuantityWriteIdentity; preview: boolean; connected: boolean; onConfirmed: () => void;
}) {
  const key = `${preview}:${identity.terminalCode}:${identity.operatorId}:${identity.sessionId}`;
  const [state, setState] = useState<{ key: string; items: PendingQuantityWrite[] }>({ key: "", items: [] });
  const [storageError, setStorageError] = useState<string | null>(null);
  const [lastConfirmedAt, setLastConfirmedAt] = useState<number | null>(null);
  const context = useRef({ identity, preview, connected, onConfirmed, key });
  context.current = { identity, preview, connected, onConfirmed, key };
  const sending = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const initialized = useRef<string | null>(null);
  const refresh = useCallback(async () => {
    const current = context.current;
    try {
      const items = await listQuantityOutbox(current.preview, current.identity);
      if (mounted.current && context.current.key === current.key) setState({ key: current.key, items });
    } catch (error) {
      if (mounted.current && context.current.key === current.key) setStorageError(error instanceof Error ? error.message : "Bekleyen cihaz kayıtları okunamadı.");
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    initialized.current = null;
    setLastConfirmedAt(null);
    void importLegacyQuantityOutbox(preview, identity).then(async () => {
      if (!mounted.current || context.current.key !== key) return;
      initialized.current = key; setStorageError(null); await refresh();
    }).catch((error: unknown) => {
      if (mounted.current && context.current.key === key) setStorageError(error instanceof Error ? error.message : "Yerel kayıtlar açılamadı. Yeni kayıt gönderilmedi.");
    });
    const changed = () => { void refresh(); };
    window.addEventListener(localDatabaseChangedEvent, changed);
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(localDatabaseChangedEvent);
    if (channel) channel.onmessage = changed;
    const interval = window.setInterval(changed, 2_000);
    return () => { mounted.current = false; sending.current?.abort(); window.clearInterval(interval);
      window.removeEventListener(localDatabaseChangedEvent, changed); channel?.close(); };
  }, [key, preview, refresh, identity.operatorId, identity.sessionId, identity.terminalCode]);

  const confirm = useCallback(async (item: PendingQuantityWrite, source: typeof context.current) => {
    await finishQuantityAttempt(source.preview, item, null);
    if (mounted.current && context.current.key === source.key) {
      setLastConfirmedAt(Date.now()); source.onConfirmed(); await refresh();
    }
  }, [refresh]);
  const verify = useCallback(async (id: string): Promise<void> => {
    const source = context.current;
    if (!source.connected) return;
    let items: PendingQuantityWrite[];
    try { items = await listQuantityOutbox(source.preview, source.identity); }
    catch (error) { if (mounted.current && context.current.key === source.key) setStorageError(error instanceof Error ? error.message : "Cihaz kayıtları okunamadı."); return; }
    const item = items.find((candidate) => candidate.idempotencyKey === id);
    if (!item) return;
    const controller = new AbortController(), timeout = window.setTimeout(() => controller.abort(), 15_000);
    try {
      const result = receiptSchema.parse(await productionGateway.quantityReceipt(item.runId, id, item.sessionId, item.terminalCode, controller.signal));
      if (result.status === "CONFIRMED" && result.entry && result.entry.enteredBy === item.operatorId &&
          result.entry.operatorSessionId === item.sessionId && result.entry.quantity === Number(item.draft.quantity) &&
          result.entry.unit === item.draft.unit && result.entry.type === item.draft.type) await confirm(item, source);
      else await finishQuantityAttempt(source.preview, item, { ...item,
        error: "Bu kontrolde sunucuda kayıt bulunamadı. Önceki istek hâlâ sonuçlanabilir; bu, kaydın hiç oluşmayacağının kanıtı değildir. Aynı oturumda aynı kayıt kimliğiyle tekrar gönderilebilir." });
    } catch (error) {
      await finishQuantityAttempt(source.preview, item, { ...item, error: error instanceof Error ? error.message : "Kayıt sonucu doğrulanamadı." }).catch(() => undefined);
    } finally { window.clearTimeout(timeout); await refresh(); }
  }, [confirm, refresh]);

  const send = useCallback(async (id: string): Promise<void> => {
    const source = context.current;
    if (sending.current || !source.connected || initialized.current !== source.key) return;
    const controller = new AbortController(); sending.current = controller;
    let item: PendingQuantityWrite | null = null;
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    try {
      item = await claimQuantityOutbox(source.preview, id, source.identity);
      if (!item) return;
      if (controller.signal.aborted || context.current.key !== source.key) throw new Error("Oturum değişti; kayıt sonucu kontrol edilmeli.");
      await refresh();
      const result = receiptEntrySchema.parse(await productionGateway.addQuantity(item.runId, {
        ...item.draft, occurredAt: item.occurredAt, idempotencyKey: item.idempotencyKey, expectedSessionId: item.sessionId,
      }, item.terminalCode, controller.signal));
      if (result.enteredBy !== item.operatorId || result.operatorSessionId !== item.sessionId || result.quantity !== Number(item.draft.quantity) ||
          result.unit !== item.draft.unit || result.type !== item.draft.type) throw new Error("Sunucu yanıtı gönderilen kayıtla eşleşmedi; kayıt silinmedi.");
      await confirm(item, source);
    } catch (error) {
      const message = controller.signal.aborted ? "Gönderimin sonucu zamanında doğrulanamadı. Sunucudan kontrol edin; yeniden göndermede aynı kayıt kimliği korunur."
        : error instanceof Error ? error.message : "Kayıt gönderimi doğrulanamadı.";
      if (item) {
        try { await finishQuantityAttempt(source.preview, item, quantityAttemptFailed(item, error instanceof ApiError && rejectedCodes.has(error.code), message)); }
        catch { if (mounted.current && context.current.key === source.key) setStorageError("Gönderim durumu cihazda güncellenemedi. Kayıt silinmedi; tekrar göndermeden önce kontrol edin."); }
      } else if (mounted.current && context.current.key === source.key) setStorageError(message);
    } finally {
      window.clearTimeout(timeout);
      if (sending.current === controller) sending.current = null;
      await refresh();
    }
  }, [confirm, refresh]);

  const items = state.key === key ? state.items : [];
  // Only a never-sent, explicitly queued quantity in this exact session is resumed automatically.
  // Uncertain writes, PIN approvals, machine commands and completion are never auto-retried.
  useEffect(() => {
    if (!connected || storageError || sending.current) return;
    const next = items.find((item) => item.status === "UNSENT" && canRetryQuantityWrite(item, identity));
    if (next) void send(next.idempotencyKey);
  }, [connected, identity.operatorId, identity.sessionId, identity.terminalCode, items, send, storageError]);

  const enqueue = useCallback(async (runId: string, workOrderNo: string, draft: QuantityWriteDraft, localDraft?: LocalDraftRef) => {
    const source = context.current;
    if (initialized.current !== source.key || !source.identity.sessionId || storageError) throw new Error(storageError ?? "Cihaz deposu ve operatör oturumu henüz doğrulanmadı.");
    const item: PendingQuantityWrite = { ...source.identity, runId, workOrderNo, draft: quantityDraftSchema.parse(draft),
      idempotencyKey: localDraft?.intentId ?? crypto.randomUUID(), occurredAt: new Date().toISOString(), status: "UNSENT", error: null,
      attempts: 0, attemptedAt: null, everUncertain: false };
    await enqueueQuantityOutbox(source.preview, item, localDraft);
    await refresh();
    return item.idempotencyKey;
  }, [refresh, storageError]);
  const dismissRejected = useCallback(async (id: string) => {
    try { await dismissRejectedQuantity(context.current.preview, id, context.current.identity); await refresh(); }
    catch (error) { setStorageError(error instanceof Error ? error.message : "Kayıt kaldırılamadı."); }
  }, [refresh]);
  return { items, storageError, lastConfirmedAt, enqueue, retry: send, verify, dismissRejected,
    canRetry: (item: PendingQuantityWrite) => connected && item.status !== "FAILED" && canRetryQuantityWrite(item, identity) };
}
